import DefaultLayout from '../../components/DefaultLayout';
import { Form } from '../../../ui/Form/Form';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Button, Icon, Spinner } from '../../../ui';
import { Pagination } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ProductsTopFilter } from '../../../ui/Form/filters/ProductsTopFilter';
import { options } from '../../../config/config';
import { StyledAllFarmsPage } from './style';
import { FarmData } from '../../../types/FarmData';
import { FarmCard } from '../../../ui/Card/FarmCard';
import { useLocation, useNavigate } from 'react-router-dom';
import Banner from 'public/components/DefaultLayout/Banner';
import { useBannerState } from 'public/context';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { StyledMobileFilterFooter } from 'public/components/MobileLayout/MobileMenu/MobileFilterFooter/style';
import MobileFilter from 'public/components/MobileLayout/MobileFilter/MobileFilter';
import { FilterOptions } from 'types/FilterOptions';
import { Filter } from 'types/Filter';
import {
  useFilterDispatch,
  useFilterState,
} from 'public/context/FilterContext';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';
import { paginationNavigationRenderer } from 'utils/paginationNavigationrenderer';
import { useFarmDispatch } from 'public/context/FarmContext';
import { FarmCoordinates } from 'types/FarmData';
import PublicRoute from 'enums/publicRoutes';
import useMarketType from '../../../utils/useMarketType';
import useSearchParamsHelper from '../../../utils/useSearchParamsHelper';
import { buildFilterChangeParams } from '../../../utils/filterHelper';
import { useListPageUrlSync } from '../../../utils/useListPageUrlSync';
import { buildPath, useListPagination } from '../../../utils/listPagination';
import useScroll from 'utils/useScroll';
import { useClearFilterHandlers } from '../../../utils/useClearFilterHandlers';

export const AllFarms = () => {
  const location = useLocation();
  const navigation = useNavigate();
  const [filter, setFilter] = useState<Filter>({});
  const [total, setTotal] = useState<number>(0);
  const [farms, setFarms] = useState<FarmData[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  const dispatchFilter = useFilterDispatch();
  const { isOpenMobileFilter, isOpenMobileMenu } = useFilterState();
  const dispatchFarm = useFarmDispatch();
  const { isEmarket, marketType } = useMarketType();
  const { getProperFilterValues } = useSearchParamsHelper();
  const [prevSearch, setPrevSearch] = useState<string>('');
  const { scrollToTop } = useScroll();

  const {
    page: currentPage,
    pageSize,
    setPage,
    setPageSize,
  } = useListPagination();

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/farms',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setTotal(response.farms.total);
      setFarms(response.farms.data);
      setFilterOptions(response.filters);

      if (response.farmsCoordinates) {
        dispatchFarm({
          type: 'SAVE_PAYLOAD',
          payload: {
            farmsCoordinates: response.farmsCoordinates as FarmCoordinates[],
            refetch: () => {},
          },
          isLoading: false,
        });
      }
    },
  });

  const openCloseDrawer = useCallback(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileFilter: !isOpenMobileFilter },
    });
  }, [isOpenMobileFilter, dispatchFilter]);

  useEffect(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isFilter: true, openCloseDrawer },
    });
  }, [dispatchFilter, openCloseDrawer]);

  useListPageUrlSync({
    setFilter,
    setPrevSearch,
    prevSearch,
    scrollToTop,
    getProperFilterValues,
    filterFields: [
      'qmark',
      'delivery',
      'emarket',
      'services',
      'farm',
      'cooperative',
    ],
    onSync: ({ filterParams, page, page_size, currentSearch }) => {
      appendData({
        filter: { ...filterParams, type: marketType },
        page,
        page_size,
        fullSearch: currentSearch,
      });
    },
  });

  const handleFilterChange = (newFilter: Filter) => {
    const oldParams = new URLSearchParams(location.search);
    const params = buildFilterChangeParams({
      filter: newFilter,
      search: oldParams.get('search') || undefined,
      pageSize,
    });
    navigation(buildPath(location.pathname, params), {
      replace: true,
    });
  };

  const { clearAppliedFilters, clearDraftFilters } = useClearFilterHandlers({
    filter,
    setFilter,
    onFilterChange: handleFilterChange,
    preserveOrdering: true,
  });

  const handleShowMap = () => {
    navigation(isEmarket ? PublicRoute.MAP : PublicRoute.WHOLESALE_MAP, {
      state: {
        filters: filter,
        dataType: 'farms',
        page: currentPage,
        page_size: pageSize,
        farms: farms,
        total: total,
      },
    });
  };

  const intl = useIntl();

  const pageSizeOptions = options.filters.pageSizeOptions;

  const breadcrumbs = [
    {
      route: '/',
      name: intl.formatMessage({ id: 'breadcrumbs.home' }),
    },
    {
      route: 'all',
      name: intl.formatMessage({ id: 'breadcrumbs.farms' }),
    },
  ];

  const { banners } = useBannerState();
  const banner_4_data = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );

  const drawer = () => (
    <MobileMenuDrawer
      title={
        <div>
          <p className="title">{intl.formatMessage({ id: 'farms.filter' })}</p>
          <div className={'count-title'}>
            <span>{total}</span>
            {intl.formatMessage({ id: 'farms.farms_and_producers' })}
          </div>
        </div>
      }
      onClose={openCloseDrawer}
      open={isOpenMobileFilter}
      className="mobile-filter"
      footer={
        <StyledMobileFilterFooter>
          <Button
            label={intl.formatMessage({ id: 'general.choose' })}
            icon={<Icon name={'mobile-filter-submit-arrow-icon'} />}
            onClick={() => {
              openCloseDrawer();
              handleFilterChange(filter);
            }}
          />
        </StyledMobileFilterFooter>
      }
      closable={false}
    >
      <MobileFilter
        setFilter={setFilter}
        filter={filter}
        filterOptions={filterOptions}
        onClearFilters={clearDraftFilters}
      />
    </MobileMenuDrawer>
  );

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent className={'grey-background'}>
        <StyledAllFarmsPage>
          <DefaultThirdHeader
            label={intl.formatMessage({ id: 'form.farms_page_title' })}
            filters={
              <ProductsTopFilter
                total={total}
                per_page={pageSize}
                setFilter={handleFilterChange}
                page_size_options={pageSizeOptions}
                setPageSize={setPageSize}
                unit={'farms'}
                filters={filter}
                handleShowMap={handleShowMap}
              />
            }
            breadcrumb={breadcrumbs}
          />
          <SearchAndFilter
            rootClassName={'laptop-search-section-box'}
            scrollThreshold={310}
            isFarmSearch={true}
            isFixed={isOpenMobileMenu}
          />
          <Spinner spinning={isLoading}>
            <div className={'products-filters-container'}>
              <Form
                setFilter={handleFilterChange}
                filterOptions={filterOptions}
                filter={filter}
                onClearFilters={clearAppliedFilters}
              />
              <div className={'products-wrapper'}>
                <div className={'products'}>
                  {farms.map((farm: FarmData) => (
                    <FarmCard key={farm.id} farm={farm} hoverable mobileRow />
                  ))}
                </div>
                <div className={'pagination'}>
                  {total > pageSize && (
                    <Pagination
                      total={total}
                      showSizeChanger={false}
                      current={currentPage}
                      pageSize={pageSize}
                      onChange={(page) => setPage(page)}
                      itemRender={paginationNavigationRenderer}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="banners">
              <div className="container">
                {banner_4_data && (
                  <Banner
                    banners={[banner_4_data]}
                    className="desktop-banner"
                  />
                )}
              </div>
            </div>
          </Spinner>
        </StyledAllFarmsPage>
        {drawer()}
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
